import React from 'react';
import classnames from 'classnames';
import _ from 'underscore';
import { CopilotIcon } from '@bingads-webui-react/copilot-ui-base';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
// @ts-ignore
import { withSiteMap } from '@bingads-webui-react/with-site-map';
// @ts-ignore
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
// @ts-ignore
import { ListSelector } from '@bingads-webui-react/list-selector';
import { IQuestion } from '@bingads-webui/search-utils';
import { getSearchQuestionsFromSiteMap, getSearchQuestionsFromQuery } from './utils';

import { getCopilotResultViewStyles } from './CopilotResultView.styles';

const DEFAULT_SEARCH_RESULT_NUM = 3;
const COPILOT_SEARCH = 'copilot-search';

export interface ICopilotResultViewProps {
  classes: any;
  i18n: {
    getString: (key: string) => string;
  };
  searchQuery: string;
  siteMap: any;
  onCopilotClick?: (param: any) => void;
  clear?: () => void;
  placeholderText?: string;
  logger?: {
    trace: (param: any) => void;
  };
  isNewLogo?: boolean;
}

interface IResultViewProps {
  searchQuery: string;
  i18n: any;
  clear?: () => void;
  placeholderText?: string;
  searchActivity?: {
    trace: (param: any) => void;
  };
}

interface ICopilotResultViewState {
  copilotResults: IQuestion[];
  prevSearchQuery: string,
}

class CopilotResultView extends React.PureComponent<ICopilotResultViewProps, ICopilotResultViewState> {
  constructor(props: ICopilotResultViewProps) {
    super(props);

    this.state = {
      copilotResults: getSearchQuestionsFromSiteMap(props.siteMap, props.i18n),
      prevSearchQuery: props.searchQuery,
    };
  }

  static getDerivedStateFromProps(nextProps: ICopilotResultViewProps, prevState: ICopilotResultViewState) {
    if (prevState.prevSearchQuery !== nextProps.searchQuery) {
      if (nextProps.searchQuery) {
        return {
          copilotResults: getSearchQuestionsFromQuery(nextProps.searchQuery, nextProps.i18n),
          prevSearchQuery: nextProps.searchQuery,
        };
      }

      return {
        copilotResults: getSearchQuestionsFromSiteMap(nextProps.siteMap, nextProps.i18n),
        prevSearchQuery: nextProps.searchQuery,
      };
    }

    return null;
  }

  onSelect = (question: Pick<IQuestion, 'text'>, index: number) => {
    const customizedEvent = question.text ? {
      eventType: 'message',
      eventSource: 'globalSearchCopilotResultView',
      eventData: {
        message: question.text,
      },
    } : undefined;

    if (this.props.onCopilotClick) {
      this.props.onCopilotClick({
        customizedEvent,
        forceOpen: true,
      });
    }

    if (this.props.logger) {
      this.props.logger.trace({
        keyword: question.text,
        isSearchResultSelected: true,
        selectedItem: COPILOT_SEARCH,
        isUserInput: index < 0,
      });
    }

    const toggleCopilotEvent = new CustomEvent('copilotToggleEvent', {
      detail: customizedEvent,
      bubbles: true,
      cancelable: true,
      composed: false,
    });

    document.dispatchEvent(toggleCopilotEvent);

    if (this.props.clear) {
      this.props.clear();
    }
  };

  getCopilotListView = () => {
    const {
      searchQuery,
      i18n,
    } = this.props;

    const {
      renderItem,
      renderHeader,
      onSelect,
    } = this;

    return (
      <div>
        <ListSelector
          renderHeader={() => renderHeader(searchQuery)}
          renderItem={renderItem}
          renderDivider={_.noop}
          onSelect={onSelect}
          data={_.first(this.state.copilotResults, DEFAULT_SEARCH_RESULT_NUM)}
          enableArrowKeysSupport
          ariaLabel={i18n.getString(_TL_('Copilot result list'))}
        />
      </div>
    );
  };

  renderItem = (question: IQuestion) => {
    const { classes } = this.props;
    const itemClassName = classnames('copilot-search-result-item-text');

    return (
      <div
        className={classnames(classes.searchResultItem, 'copilot-search-result-item')}
      >
        <div className={classes.searchItemContainer}>
          <div className={classnames(classes.searchItemSpan, 'copilot-search-result-text')}>
            <span
              className={itemClassName}
              title={question.text}
            >
              {question.text}
            </span>
          </div>
        </div>
      </div>
    );
  };

  renderHeader = (query: string) => {
    const { classes, i18n } = this.props;
    const onClick = () => this.onSelect({ text: query }, -1);
    const onCopilotKeyDown = (e: any) => { if (e && e.key === 'Enter') { onClick(); } };

    return (
      <div className={classnames(classes.resultHeaderContainer, 'copilot-search-result-section-header')}>
        <span role="button" tabIndex={0} onClick={onClick} onKeyDown={onCopilotKeyDown} className={classes.resultHeader}>
          <div className={classes.resultHeaderIcon}>
            <CopilotIcon size="medium" color={this.props.isNewLogo ? 'multicolor' : 'blue'} title={i18n.getString(_TL_('Copilot icon'))} iconStyles={classes.copilotIcon} />
          </div>
          {
            query ? (
              <div className={classnames(classes.resultHeaderSpan, 'copilot-search-header-text')}>
                <span
                  title={query}
                >
                  {query}
                </span>
              </div>
            ) : (
              <div className={classnames(classes.resultHeaderPlaceholder, 'copilot-search-header-placeholder')}>
                {i18n.getString(this.props.placeholderText || _TL_('Your question will appear here'))}
              </div>
            )
          }
          <div className={classnames(classes.resultHeaderLink, 'copilot-search-header-link')}>{i18n.getString(_TL_('Ask Copilot'))}</div>
        </span>
        {
          this.state.copilotResults.length > 0 && (
            <span>
              <div className={classnames(classes.resultHeaderHint, 'copilot-search-header-hint')}>
                {i18n.getString(_TL_('You can also ask Copilot:'))}
              </div>
            </span>
          )
        }
      </div>
    );
  };

  render() {
    const {
      classes,
      i18n,
      searchQuery,
    } = this.props;

    const searchResultItemCount = this.state.copilotResults.length;
    const emptyText = curlyTemplate(i18n.getString(_TL_('No results found for {{searchText}}')))({
      searchText: searchQuery,
    });
    const resultText = curlyTemplate(i18n.getString(_TL_('Found {{totalCount}} Results for {{searchText}}')))({
      totalCount: searchResultItemCount,
      searchText: searchQuery,
    });
    const liveRegionText = searchResultItemCount === 0 ? emptyText : resultText;

    return (
      <div className={classes.searchResultDropDown}>
        {this.getCopilotListView()}
        <div
          className="search-result live-region"
          aria-label={liveRegionText}
        />
      </div>
    );
  }
}

export const StyledCopilotResultView = withI18nConsumer(withDefaultStyles(withSiteMap(CopilotResultView), getCopilotResultViewStyles));
export const CopilotResultViewExternalSiteMap = withI18nConsumer(withDefaultStyles(CopilotResultView, getCopilotResultViewStyles));

export const renderCopilotSearch = (props: IResultViewProps) => (
  <StyledCopilotResultView
    searchQuery={props.searchQuery}
    i18n={props.i18n}
    key="copilot-result-view"
    clear={props.clear}
    placeholderText={props.placeholderText}
    logger={props.searchActivity}
  />
);

export const renderCopilotSearchWithCopilot = (onCopilotClick: (param: any) => void, isNewLogo?: boolean) => (props: IResultViewProps) => (
  <StyledCopilotResultView
    searchQuery={props.searchQuery}
    i18n={props.i18n}
    key="copilot-result-view"
    clear={props.clear}
    onCopilotClick={onCopilotClick}
    placeholderText={props.placeholderText}
    logger={props.searchActivity}
    isNewLogo={isNewLogo}
  />
);
